import React, {useContext, useRef, useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {FilterContext} from "../FilterContextProvider";
import Button from "../Components/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faArrowLeft, faSpinner} from "@fortawesome/free-solid-svg-icons";
import ReportsLayout from "./ReportsLayout";
import ReactToPrint from "react-to-print";
import {exportToCSV} from "../Utility/Csv";
import useApi from "../Api/Api";
import {UserContext} from "../Auth/UserContextProvider";
import useEvents from "../hooks/useEvents";
import EventFilter from "../Sales/EventFilter";

const Tour = () => {
  const {filterData, setFilterData} = useContext(FilterContext);
  const printRef = useRef();
  const [salesEventIds, setSalesEventIds] = useState({});
  const api = useApi();
  const [sales, setSales] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [startingEvents, setStartingEvents] = useState({});
  const [eventFilterDate, setEventFilterDate] = useState({start_date: "", end_date: "", date_button: ""});
  const {isAdmin} = useContext(UserContext);

  const {
    events,
    eventIds,
    setEventIds,
    isLoading,
  } = useEvents("show", "", false, false);

  const setEvent = (id) => {
    setFilterData({...filterData, event_id: id});
  };

  const noMatch = {
    gross_cost: 0,
  };

  events.forEach((event) => {
    const match =
      sales.find(({event_id}) => event_id === event.id) || noMatch;
    event.cogs = match.gross_cost;
    if (!event.tax_kept_by_venue && !event.remitted) {
      event.tax_net = 0;
    }
  });

  const eventsToExport = events.map((event) => ({
    Date: event.formattedDate,
    City: event.city,
    State: event.state,
    Gross_sales: event.gross_sales,
    Tax: event.tax_net,
    Event_fees: event.fee_net,
    CC_fee: event.cc_net,
    Hall_fee: event.hall_net,
    Total_to_artist: event.total_to_artist,
    Tax_kept: event.tax_kept_by_venue,
    Artist_remits: event.remitted,
    Attendance: event.attendance,
    Per_cap: event.gross_sales / event.attendance,
  }));

  const totals = {
    Date: "Totals",
    City: "",
    State: "",
    Gross_sales: events.reduce((a, b) => {
      return a + b.gross_sales;
    }, 0),
    Tax: events.reduce((a, b) => {
      return a + b.tax_net;
    }, 0),
    Event_fees: events.reduce((a, b) => {
      return a + b.fee_net;
    }, 0),
    CC_fee: events.reduce((a, b) => {
      return a + b.cc_net;
    }, 0),
    Hall_fee: events.reduce((a, b) => {
      return a + b.hall_net;
    }, 0),
    Total_to_artist: events.reduce((a, b) => {
      return a + b.total_to_artist;
    }, 0),
    Tax_kept: "",
    Artist_remits: "",
    Attendance: events.reduce((a, b) => {
      return a + b.attendance;
    }, 0),
    Per_cap:
      events
        .filter((each) => each.attendance > 0)
        .reduce((a, b) => {
          return a + b.gross_sales / b.attendance;
        }, 0) / events.length,
  };

  const combined = eventsToExport.concat(totals);

  const adminEventsToExport = events.map((event) => ({
    Date: event.formattedDate,
    City: event.city,
    State: event.state,
    Gross_sales: event.gross_sales,
    Tax: event.tax_net,
    Event_fees: event.fee_net,
    CC_fee: event.cc_net,
    Hall_fee: event.hall_net,
    Total_to_artist: event.total_to_artist,
    Total_from_venue: event.total_from_venue,
    COGS: event.cogs,
    Artist_profit: event.total_to_artist - event.cogs,
    Tax_kept: event.tax_kept_by_venue,
    Artist_remits: event.remitted,
    Attendance: event.attendance,
    Per_cap: event.gross_sales / event.attendance,
  }));

  const adminTotals = {
    Date: "Totals",
    City: "",
    State: "",
    Gross_sales: events.reduce((a, b) => {
      return a + b.gross_sales;
    }, 0),
    Tax: events.reduce((a, b) => {
      return a + b.tax_net;
    }, 0),
    Event_fees: events.reduce((a, b) => {
      return a + b.fee_net;
    }, 0),
    CC_fee: events.reduce((a, b) => {
      return a + b.cc_net;
    }, 0),
    Hall_fee: events.reduce((a, b) => {
      return a + b.hall_net;
    }, 0),
    Total_to_artist: events.reduce((a, b) => {
      return a + b.total_to_artist;
    }, 0),
    Total_from_venue: events.reduce((a, b) => {
      return a + b.total_from_venue;
    }, 0),
    COGS: events.reduce((a, b) => {
      return a + b.cogs;
    }, 0),
    Artist_profit: events.reduce((a, b) => {
      return a + b.total_to_artist - b.cogs;
    }, 0),
    Tax_kept: "",
    Artist_remits: "",
    Attendance: events.reduce((a, b) => {
      return a + b.attendance;
    }, 0),
    Per_cap:
      events
        .filter((each) => each.attendance > 0)
        .reduce((a, b) => {
          return a + b.gross_sales / b.attendance;
        }, 0) / events.length,
  };

  useEffect(() => {
    setSalesEventIds(eventIds);
  }, [eventIds]);

  useEffect(() => {
    const obj = {};
    events.forEach((each) => (obj[each.id] = true));
    if(Object.keys(salesEventIds).length === 0){
      setSalesEventIds(obj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  useEffect(() => {
    const queryString = Object.keys(salesEventIds)
      .map((id) => `event_ids[]=${id}`)
      .join("&");
    Object.keys(salesEventIds).length > 0 &&
    api
      .get(
        `/sales/eventcost?artist_id=${filterData.artist_id}&${queryString}`
      )
      .then((r) => {
        setSales(r.data);
        setErrorMessage(false);
      })
      .catch(() => {
        setErrorMessage(
          "The current date range includes too many events for the COGS column to be able to calculate. Narrow your date range for better results."
        );
      });
  }, [api, filterData.artist_id, salesEventIds]);

  const adminCombined = adminEventsToExport.concat(adminTotals);

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {events.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <div className="ml-2">
              {isAdmin ? (
                <Button
                  className="button button-white"
                  text="Export"
                  onClick={() =>
                    exportToCSV(adminCombined, `${filterData.artist_name}_tour`)
                  }
                ></Button>
              ) : (
                <Button
                  className="button button-white"
                  text="Export"
                  onClick={() =>
                    exportToCSV(combined, `${filterData.artist_name}_tour`)
                  }
                ></Button>
              )}
            </div>
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
              reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col w-1/2">
          <span className="text-xs pl-12">Date range/show</span>
          <EventFilter
            events={events}
            eventsToRender={startingEvents}
            setEventsToRender={setStartingEvents}
            filterData={filterData}
            eventIds={eventIds}
            eventFilterDate={eventFilterDate}
            setEventFilterDate={setEventFilterDate}
            setEventIds={setEventIds}
            setFilterData={setFilterData}
          ></EventFilter>
        </div>
        {errorMessage && <div className="text-red-500">{errorMessage}</div>}
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-base mb-2 border-2 border-black p-2">
            <div className="w-1/3 text-xl font-extrabold mr-4">
              TM Tour Report
            </div>
            <div className="w-1/3">
              <div className="text-lg flex self-center">
                <div className="mr-2 italic">Artist:</div>
                {filterData.artist_name}
              </div>
            </div>
            <div className=" w-1/3">
              <div className="self-center flex font-normal">
                <div className="mr-2 italic">Start Date:</div>
                {filterData.start_date?.slice(5, 7)}/
                {filterData.start_date?.slice(8, 10)}/
                {filterData.start_date?.slice(0, 4)}
              </div>
              <div className="self-center flex font-normal">
                <div className=" mr-4 italic">End Date:</div>
                {filterData.end_date?.slice(5, 7)}/
                {filterData.end_date?.slice(8, 10)}/
                {filterData.end_date?.slice(0, 4)}
              </div>
            </div>
          </div>
         
          {events.length > 0 && !isLoading && (
            <div>
              <table>
                <thead className="text-xs font-light">
                  <tr className="text-right border-b-2 border-black h-11">
                    <td className="w-1/16 text-left pr-2">DATE</td>
                    <td className="w-1/8 text-left">CITY</td>
                    <td className="w-1/25 text-left">STATE</td>
                    <td className="w-1/16">GROSS SALES</td>
                    <td className="w-1/16">TAX</td>
                    <td className="w-1/16">EVENT FEES</td>
                    <td className="w-1/16">CC FEE</td>
                    <td className="w-1/16">HALL FEE</td>
                    <td className="w-1/16">TOTAL TO ARTIST</td>
                    {isAdmin && (
                      <>
                        <td className="w-1/16">TOTAL FROM VENUE</td>
                        <td className="w-1/16">COGS</td>
                        <td className="w-1/16">PROFIT TO ARTIST</td>
                      </>
                    )}
                    <td className="w-1/25 text-center">VENUE KEPT TAX</td>
                    <td className="w-1/25 text-center">ARTIST REMITS</td>
                    <td className="w-1/16">ATTENDANCE</td>
                    <td className="w-thin">PER CAP</td>
                  </tr>
                </thead>
                <tbody>
                  {events.map((eventToRender, i) => {
                    return (
                      <tr
                        key={eventToRender.id}
                        className="border-b border-black text-right text-sm h-10"
                      >
                        <td id={`date_${i}`} className="text-left pr-2">
                          <Link
                            to={`/reports/events/${eventToRender.id}/sales/`}
                            onClick={() => setEvent(eventToRender.id)}
                          >
                            {eventToRender.formattedDate}
                          </Link>
                        </td>
                        <td id={`city_${i}`} className="text-left">
                          {eventToRender.city}
                        </td>
                        <td id={`state_${i}`} className="text-left">
                          {eventToRender.state}
                        </td>
                        <td>
                          {eventToRender.gross_sales?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td>
                          {eventToRender.tax_kept_by_venue ||
                          eventToRender.remitted
                            ? eventToRender.tax_net?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                            : 0?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                        </td>
                        <td>
                          {eventToRender.fee_net?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td>
                          {eventToRender.cc_net?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td>
                          {eventToRender.hall_net?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td>
                          {eventToRender.total_to_artist?.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        {isAdmin && (
                          <>
                            <td>
                              {eventToRender.total_from_venue?.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                }
                              )}
                            </td>
                            <td>
                              {eventToRender.cogs?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </td>
                            <td>
                              {(
                                eventToRender.total_to_artist -
                                eventToRender.cogs
                              )?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </td>
                          </>
                        )}

                        <td className="text-center">
                          {eventToRender.tax_kept_by_venue && (
                            <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                          )}
                        </td>
                        <td className="text-center">
                          {eventToRender.remitted && (
                            <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                          )}
                        </td>
                        <td id={`attendance_${i}`}>
                          {eventToRender.attendance &&
                          eventToRender.attendance?.toLocaleString()}
                        </td>
                        <td>
                          {eventToRender.gross_sales && eventToRender.attendance
                            ? (
                              eventToRender.gross_sales /
                              eventToRender.attendance
                            )?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                            : 0?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="text-right h-11 w-full text-xs">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>GROSS SALES</td>
                    <td>TAX</td>
                    <td>EVENT FEES</td>
                    <td>CC FEE</td>
                    <td>HALL FEE</td>
                    <td>TOTAL TO ARTIST</td>
                    {isAdmin && (
                      <>
                        <td>TOTAL FROM VENUE</td>
                        <td>COGS</td>
                        <td>PROFIT TO ARTIST</td>
                      </>
                    )}
                    <td></td>
                    <td></td>
                    <td>ATTENDANCE</td>
                    <td>PER CAP</td>
                  </tr>
                  <tr className="text-right h-11 w-full text-sm">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.gross_sales;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.tax_net;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.fee_net;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.cc_net;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.hall_net;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.total_to_artist;
                        }, 0)
                        ?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                    </td>
                    {isAdmin && (
                      <>
                        <td>
                          {events
                            .reduce((a, b) => {
                              return a + b.total_from_venue;
                            }, 0)
                            ?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                        </td>
                        <td>
                          {events
                            .reduce((a, b) => {
                              return a + b.cogs;
                            }, 0)
                            ?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                        </td>
                        <td>
                          {events
                            .reduce((a, b) => {
                              return a + b.total_to_artist - b.cogs;
                            }, 0)
                            ?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                        </td>
                      </>
                    )}
                    <td></td>
                    <td></td>
                    <td>
                      {events
                        .reduce((a, b) => {
                          return a + b.attendance;
                        }, 0)
                        ?.toLocaleString()}
                    </td>
                    <td>
                      {(
                        events
                          .filter((each) => each.attendance > 0)
                          .reduce((a, b) => {
                            return a + b.gross_sales / b.attendance;
                          }, 0) / events.length
                      )?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {isLoading && (<div className="mx-12 py-16">
            <div className="w-full flex justify-center h-full items-center"><FontAwesomeIcon icon={faSpinner}
                                                                                             spin></FontAwesomeIcon>
            </div>
          </div>)}

          {events.length === 0 && !isLoading && (<div className="mx-12 py-16">
            There are no events for this artist over this date range
          </div>)}

        </div>
      </div>
    </ReportsLayout>
  );
};

export default Tour;
